import { useTranslation } from "react-i18next"

import { RefreshCw, TriangleAlert } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"

import {
  HOSTED_AGENT_EVAL_COOLDOWN_SECONDS,
  HOSTED_AGENT_EVAL_RUNS_PER_DAY,
  planHostedAgentEvalRetry,
} from "../utils/hosted-agent-eval-run-state"

// Everything that can go wrong around a suite run, in its own file (#3028).
//
// Two families, deliberately separate:
//   * the suite was REFUSED — a stable server code, one piece of copy each;
//   * the suite may be running fine but we cannot READ it — which must never
//     be left as a spinner that never resolves.

/**
 * A refusal, keyed off the server's stable `code`. The 429 is the interesting
 * one: the backend enforces both a ~60 s cooldown and a 12-per-24-h cap, and
 * the daily cap can answer with most of a day's worth of seconds. Raw seconds
 * are unreadable at that scale, so the wait is re-expressed in its own unit and
 * the two bounds get different sentences.
 */
export function HostedAgentEvalRunError({
  code,
  message,
  retrySeconds,
}: {
  code: string
  message: string | null
  retrySeconds: number | null
}) {
  const { t } = useTranslation()

  let title = t("adminDashboard.hostedAgents.evals.run.errorTitle", {
    defaultValue: "The suite could not be started",
  })
  let body: string | null = null

  switch (code) {
    case "eval_rate_limited":
      title = t("adminDashboard.hostedAgents.evals.run.rateLimitTitle", {
        defaultValue: "Too soon to run again",
      })
      body = rateLimitBody(t, retrySeconds)
      break
    case "agent_paused":
      title = t("adminDashboard.hostedAgents.evals.run.pausedTitle", {
        defaultValue: "The agent is paused",
      })
      body = t("adminDashboard.hostedAgents.evals.run.paused", {
        defaultValue:
          "A paused agent cannot run its evals. Resume it from the Lifecycle tab, then run the suite again.",
      })
      break
    case "selftest_in_flight":
      body = t("adminDashboard.hostedAgents.evals.run.selftestInFlight", {
        defaultValue:
          "A self-test is already running for this agent. Wait for the Test tab to finish, then start the suite.",
      })
      break
    case "eval_suite_in_flight":
      body = t("adminDashboard.hostedAgents.evals.run.suiteInFlight", {
        defaultValue: "An eval suite is already running for this agent. Wait for it to finish.",
      })
      break
    case "no_eval_cases":
      body = t("adminDashboard.hostedAgents.evals.run.noEnabledCases", {
        defaultValue: "This agent has no enabled eval cases to run.",
      })
      break
    case "eval_service_unavailable":
      body = t("adminDashboard.hostedAgents.evals.run.unavailable", {
        defaultValue:
          "The eval service is not connected. Deploy and connect the agent's container first.",
      })
      break
    default:
      body = message
  }

  return (
    <Alert
      variant={code === "eval_rate_limited" || code === "agent_paused" ? "default" : "destructive"}
    >
      <TriangleAlert className="text-foreground size-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{body}</AlertDescription>
    </Alert>
  )
}

type Translate = ReturnType<typeof useTranslation>["t"]

/** The remaining wait in the unit it is worth stating in. */
function formatWait(t: Translate, plan: ReturnType<typeof planHostedAgentEvalRetry>): string {
  if (plan.unit === "hours") {
    return t("adminDashboard.hostedAgents.evals.run.waitHours", {
      defaultValue: "{{value}} h",
      value: plan.value,
    })
  }
  if (plan.unit === "minutes") {
    return t("adminDashboard.hostedAgents.evals.run.waitMinutes", {
      defaultValue: "{{value}} min",
      value: plan.value,
    })
  }
  return t("adminDashboard.hostedAgents.evals.run.waitSeconds", {
    defaultValue: "{{value}} s",
    value: plan.value,
  })
}

/** The 429 sentence: which bound was hit, and how long the wait really is. */
function rateLimitBody(t: Translate, retrySeconds: number | null): string {
  if (retrySeconds === null || retrySeconds <= 0) {
    return t("adminDashboard.hostedAgents.evals.run.rateLimitReady", {
      defaultValue: "The wait is over — you can start the suite again.",
    })
  }
  const plan = planHostedAgentEvalRetry(retrySeconds)
  const wait = formatWait(t, plan)

  if (plan.limit === "cooldown") {
    return t("adminDashboard.hostedAgents.evals.run.rateLimitCooldown", {
      defaultValue:
        "This agent ran a suite moments ago. Wait {{wait}} — one run per {{cooldown}} s keeps a double-click from spending the budget twice.",
      wait,
      cooldown: HOSTED_AGENT_EVAL_COOLDOWN_SECONDS,
    })
  }
  return t("adminDashboard.hostedAgents.evals.run.rateLimitDaily", {
    defaultValue:
      "This agent has used all {{max}} eval suites allowed in 24 hours. The next slot opens in about {{wait}}.",
    max: HOSTED_AGENT_EVAL_RUNS_PER_DAY,
    wait,
  })
}

/**
 * The run cannot be read. This exists because the 202 document is seeded into
 * the cache as `running`: without this the panel would sit on a spinner and a
 * "0 of N cases reported" line for ever while every poll quietly 404s.
 */
export function HostedAgentEvalRunReadError({
  gaveUp,
  onRetry,
}: {
  /** True once polling stopped after repeated failures. */
  gaveUp: boolean
  onRetry: () => void
}) {
  const { t } = useTranslation()
  return (
    <Alert>
      <TriangleAlert className="text-foreground size-4" />
      <AlertTitle>
        {t("adminDashboard.hostedAgents.evals.run.readErrorTitle", {
          defaultValue: "The run could not be read",
        })}
      </AlertTitle>
      <AlertDescription className="space-y-2">
        <p>
          {gaveUp
            ? t("adminDashboard.hostedAgents.evals.run.readErrorGaveUp", {
                defaultValue:
                  "Several attempts to read this run failed, so we stopped checking. The suite may still be running on the server — the result will appear under Previous runs.",
              })
            : t("adminDashboard.hostedAgents.evals.run.readErrorRetrying", {
                defaultValue:
                  "The last status check failed. Nothing below is up to date until it succeeds again.",
              })}
        </p>
        <Button size="sm" type="button" variant="outline" onClick={onRetry}>
          <RefreshCw className="size-4" />
          {t("adminDashboard.hostedAgents.evals.run.readErrorRetry", {
            defaultValue: "Check again",
          })}
        </Button>
      </AlertDescription>
    </Alert>
  )
}

/**
 * The run document still says `running` long after any plausible suite could
 * have finished — most likely its background task died without settling the
 * document. Stating that beats an eternal spinner.
 */
export function HostedAgentEvalRunStalled({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <Alert>
      <TriangleAlert className="text-foreground size-4" />
      <AlertTitle>
        {t("adminDashboard.hostedAgents.evals.run.stalledTitle", {
          defaultValue: "This run stopped reporting",
        })}
      </AlertTitle>
      <AlertDescription className="space-y-2">
        <p>
          {t("adminDashboard.hostedAgents.evals.run.stalledBody", {
            defaultValue:
              "The run has claimed to be in progress far longer than a suite can take, so we stopped watching it. Whatever was measured is below; treat the rest as unmeasured.",
          })}
        </p>
        <Button size="sm" type="button" variant="outline" onClick={onRetry}>
          <RefreshCw className="size-4" />
          {t("adminDashboard.hostedAgents.evals.run.readErrorRetry", {
            defaultValue: "Check again",
          })}
        </Button>
      </AlertDescription>
    </Alert>
  )
}
